import { Provider } from "@/components/patient/ProviderCard";
import { TimeSlot } from "@/components/patient/TimeSlotSelector";
import { addDays, format } from "date-fns";
import { TriageOutcome } from "@/types/triage";

// Mock provider data with specialties aligned to common symptoms
export const mockProviders: Provider[] = [
  {
    id: "p1",
    name: "Dr. Sarah Chen",
    specialty: "Internal Medicine",
    experience: "15 years",
    rating: 4.9,
    matchScore: 95,
    imageUrl: "/assets/providers/doctor-1.jpg",
    nextAvailable: "Today",
  },
  {
    id: "p2",
    name: "Dr. Michael Rodriguez",
    specialty: "Family Medicine",
    experience: "8 years",
    rating: 4.7,
    matchScore: 88,
    imageUrl: "/assets/providers/doctor-2.jpg",
    nextAvailable: "Tomorrow",
  },
  {
    id: "p3",
    name: "Dr. Emily Johnson",
    specialty: "Cardiology",
    experience: "12 years",
    rating: 4.8,
    matchScore: 75,
    imageUrl: "/assets/providers/doctor-3.jpg",
    nextAvailable: "In 2 days",
  },
  {
    id: "p4",
    name: "Dr. David Kim",
    specialty: "Pulmonology",
    experience: "10 years",
    rating: 4.6,
    matchScore: 82,
    imageUrl: "/assets/providers/doctor-4.jpg",
    nextAvailable: "Today",
  },
  {
    id: "p5",
    name: "Dr. Lisa Patel",
    specialty: "Gastroenterology",
    experience: "14 years",
    rating: 4.9,
    matchScore: 70,
    imageUrl: "/assets/providers/doctor-5.jpg",
    nextAvailable: "In 3 days",
  },
];

// Generate time slots for a given date
export const generateTimeSlots = (date: Date): TimeSlot[] => {
  const morningTimes = ["8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM"];
  const afternoonTimes = ["1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM"];
  const allTimes = [...morningTimes, ...afternoonTimes];
  
  // Create random availability pattern
  return allTimes.map((time, index) => {
    // Make some slots unavailable randomly, but ensure at least 5 slots are available
    const isAvailable = index < 5 ? true : Math.random() > 0.4;
    
    return {
      id: `slot-${date.toISOString()}-${time}`,
      time,
      duration: 30, // Default duration
      isAvailable,
    };
  });
};

// Generate available dates (next 14 days)
export const generateAvailableDates = (): string[] => {
  const dates: string[] = [];
  const today = new Date();
  
  for (let i = 0; i < 14; i++) {
    const date = addDays(today, i);
    // Skip weekends for simplicity
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      dates.push(format(date, "yyyy-MM-dd"));
    }
  }
  
  return dates;
};

// Filter providers based on triage results
export const getRecommendedProviders = (triageOutcome: TriageOutcome | null): Provider[] => {
  if (!triageOutcome) return mockProviders;
  
  // Clone the providers array to avoid modifying the original
  const providers = [...mockProviders];
  
  // Adjust match scores based on triage outcome
  return providers.map(provider => {
    let adjustedScore = provider.matchScore;
    
    // Adjust score based on urgency
    switch (triageOutcome.urgency) {
      case "immediate":
        // For immediate cases, prioritize internal medicine and cardiology
        if (provider.specialty === "Internal Medicine" || provider.specialty === "Cardiology") {
          adjustedScore += 10;
        }
        break;
      case "urgent":
        // For urgent cases, slightly boost family medicine
        if (provider.specialty === "Family Medicine") {
          adjustedScore += 5;
        }
        break;
      default:
        // No adjustment for other urgency levels
        break;
    }
    
    // Adjust score based on primary symptoms (simplified example)
    if (triageOutcome.primarySymptoms?.includes("chest") && provider.specialty === "Cardiology") {
      adjustedScore += 15;
    }
    
    if (triageOutcome.primarySymptoms?.includes("breath") && provider.specialty === "Pulmonology") {
      adjustedScore += 15;
    }
    
    if (triageOutcome.primarySymptoms?.includes("stomach") && provider.specialty === "Gastroenterology") {
      adjustedScore += 15;
    }
    
    // Cap the score at 100
    adjustedScore = Math.min(100, adjustedScore);
    
    return {
      ...provider,
      matchScore: adjustedScore
    };
  }).sort((a, b) => b.matchScore - a.matchScore); // Sort by match score descending
};

// Mock patient info
export const mockPatientInfo = {
  firstName: "Alex",
  lastName: "Morgan",
  email: "alex.morgan@example.com",
  phone: "(555) 123-4567",
  dateOfBirth: "1985-06-15",
  address: "123 Main St, Anytown, CA 94123"
};
